import { bootstrap } from "@usable/office-shell";
import { deliverImageAttachment, type ImageAttachmentRule } from "@usable/office-embed";
import { getSupportedPptxToolSchemas, handlePptxToolCall } from "./lib/pptx-tools";
import { pptxContextProvider } from "./lib/pptx-context";
import { recordGeneratedImage } from "./lib/generated-images";
import { installAttachments, installStagedData, installWorkspaceFiles } from "./lib/staged-data";

// The whole PowerPoint add-in: host-specific tools + config; the shared shell does the rest.
// Logo + ribbon icons come from the shared @usable/office-assets brand set.
//
// PowerPoint is the one host that exercises the shared `addImage` embed capability:
// `get_slide_screenshot` returns a base64 PNG, which we deliver to the chat (via the
// capabilities handed to onToolCall) and strip from the tool result so the AI never
// receives a giant unusable blob. `submit: true` auto-submits the screenshot as a
// user turn so the model actually SEES the rendered slide and can critique/fix it —
// the self-verify loop the system prompt relies on. Excel/Word ignore the
// capabilities argument entirely — this is an opt-in extension, not a fork.
const IMAGE_RULES: ImageAttachmentRule[] = [
  {
    tool: "get_slide_screenshot",
    field: "base64",
    mimeType: "image/png",
    fileName: "current-slide.png",
    submit: true,
  },
];

// Capability gating: the tool schemas must be filtered by what the running
// host actually supports, and `Office.context.requirements` is only reliable
// AFTER Office is ready. So we resolve the gated schemas inside onReady and
// pass them to bootstrap. bootstrap's own internal `Office.onReady` then
// resolves immediately (nesting is safe). Mirrors apps/word (PR #14).
Office.onReady(() => {
  const toolSchemas = getSupportedPptxToolSchemas();
  logPptxApiSupportMatrix(toolSchemas.length);

  bootstrap({
    appName: "PowerPoint Assistant",
    documentNoun: "presentation",
    embedToken:
      process.env.EMBED_TOKEN ??
      "uc_c0d33e3eebfd2e615b6e2d6a4354c22fd10aa8051111f4bfa92b5c0303a342a5",
    dbName: "usable-powerpoint-chat-history",
    toolSchemas,
    onToolCall: async (tool, args, caps) => {
      installStagedData(caps.stagedData); // uploaded-file access for image/logo sources
      installAttachments(caps.attachments); // normal paperclip uploads (stay multimodal)
      installWorkspaceFiles(caps.workspaceFiles); // Usable workspace assets by fileId (v1.172.0)
      const result = await handlePptxToolCall(tool, args, caps.requestId);
      return deliverImageAttachment(tool, result, caps, IMAGE_RULES);
    },
    contextProvider: pptxContextProvider,
    // Cache AI-generated images pushed from the embed so insert_image /
    // compose_slide can use them directly (chat file URLs are CORS-blocked).
    onImageGenerated: recordGeneratedImage,
  });
});

/** Dev aid: log which PowerPointApi requirement sets this host supports + advertised tool count. */
function logPptxApiSupportMatrix(advertisedToolCount: number): void {
  try {
    const sets = ["1.1", "1.2", "1.3", "1.4", "1.5", "1.6", "1.7", "1.8", "1.9", "1.10"];
    const matrix = sets.map(
      (s) => `${s}:${Office.context.requirements.isSetSupported("PowerPointApi", s) ? "✓" : "✗"}`
    );
    // eslint-disable-next-line no-console
    console.log(
      `[pptx-tools] PowerPointApi support — ${matrix.join("  ")} — advertising ${advertisedToolCount} tools`
    );
  } catch {
    // requirements API unavailable (e.g. very old host) — gating already degrades
    // to "supported", so nothing to log.
  }
}
